import { action, observable } from 'mobx';

import { RouterStore } from '@estee/elc-router';
import { ViewController } from '@estee/elc-service';

import { OtpIdentifier, VerifyOtpRequest } from '~api/RampPgApiSdk';
import {
    DEFAULT_DIGIT_OF_VERIFICATION_CODE,
    DEFAULT_FLASH_DURATION,
    DEFAULT_STATUS,
    genericErrorMessage,
    OTP_ERROR_MESSAGE_TOO_MANY_ATTEMPTS_TRANSLATION_KEY,
    OTP_FLOW,
    OTP_TYPE_EMAIL,
    OTP_TYPE_EMAIL_SMS,
    OTP_TYPE_SMS,
    OTP_TYPE_SMS_EMAIL,
    OtpType,
    RESEND_OTP_AFTER,
    RiskLevels,
    SENT_STATUS,
    VERIFIED_STATUS
} from '~constants/Constants';
import { View } from '~domain/entities/SignInWithOtpFormStore';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { MessageGroup, MessageRepository } from '~repositories/MessageRepository';
import { UserRepository } from '~repositories/UserRepository';

import { OtpStateData, OtpStateHelper } from './OtpStateHelper';
import { setCookie } from '~utils/CookieUtils';

export interface IOtpViewController {
    routerStore: RouterStore;
    userRepository: UserRepository;
    configRepository: ConfigRepository;
    otpStateHelper: OtpStateHelper;
    messageRepository: MessageRepository;
}

type OtpData = OtpStateData & {
    allowSwitch: boolean;
    enableCountdown: boolean;
    countdownSeconds: number;
    resendCodeBlockThreshold: number;
    numberOfBox: number;
    sendToText: string;
    changeToTranslationKey: string;
    switchToTranslationKey: string;
    flow: OTP_FLOW;
};

export abstract class OtpViewController extends ViewController {
    protected userRepository: UserRepository;
    protected configRepository: ConfigRepository;
    protected messageRepository: MessageRepository;
    private otpStateHelper: OtpStateHelper;
    private routerStore: RouterStore;
    @observable private allowSwitch: boolean;
    @observable private numberOfBox: number;
    @observable private enableCountdown: boolean = false;
    @observable private countdownSeconds: number = 0;
    @observable private resendCodeBlockThreshold: number = 0;

    abstract get switchToOtpType(): OtpType;
    abstract get requestBase(): OtpIdentifier;
    abstract saveRequestIdIntoUserStore(id: string): void;

    constructor({
        userRepository,
        configRepository,
        otpStateHelper,
        messageRepository,
        routerStore
    }: IOtpViewController) {
        super();

        this.userRepository = userRepository;
        this.configRepository = configRepository;
        this.otpStateHelper = otpStateHelper;
        this.messageRepository = messageRepository;
        this.routerStore = routerStore;
    }

    public get data() {
        const { data: otpStateData } = this.otpStateHelper;
        const {
            data: { flow }
        } = this.userRepository.otpStore;

        return {
            ...otpStateData,
            allowSwitch: this.allowSwitch,
            enableCountdown: this.enableCountdown,
            countdownSeconds: this.countdownSeconds,
            resendCodeBlockThreshold: this.resendCodeBlockThreshold,
            numberOfBox: this.numberOfBox,
            sendToText: '',
            changeToTranslationKey: '',
            switchToTranslationKey: '',
            flow
        } as OtpData;
    }

    @action
    public loaded = async () => {
        //Get from configuration
        this.loadFromConfiguration();

        if (this.userRepository.otpStore.data.flow === OTP_FLOW.SIGN_UP) {
            // Load status from generate code request
            // which happened in ramp form page
            this.loadFromOtpStore();
        }
    };

    protected get userFormStore() {
        return this.userRepository.userFormStore;
    }

    @action
    public verifyOtpCode = async (code: string) => {
        if (this.otpStateHelper.isVerified) {
            return;
        }
        this.otpStateHelper.startLoading();
        const {
            verifyCode,
            otpStore: { data: { requestId, nonce, flow, hmac } = {} },
            signInWithOtpFormStore: { setView }
        } = this.userRepository;

        const isSignUpFlow = flow === OTP_FLOW.SIGN_UP;

        if ((!isSignUpFlow && !code) || (isSignUpFlow && (!requestId || !nonce || !code))) {
            this.otpStateHelper.setErrorMessage(genericErrorMessage).stopLoading();

            return;
        }

        const request: VerifyOtpRequest = {
            ...this.requestBase,
            requestId,
            nonce,
            hmac,
            verifyCode: code
        };

        const {
            setErrorMessage,
            setSuccessMessage,
            stopLoading,
            setRetryButtonVisibility
        } = this.otpStateHelper;
        setRetryButtonVisibility(false);
        this.messageRepository.clearAllMessages();
        const result = await verifyCode(request);

        if (result) {
            const { translationKey = '', success, rs: riskLevel } = result;

            if (success) {
                this.otpStateHelper.setVerifiedCodes(code);
                switch (flow) {
                    case OTP_FLOW.RESET_PASSWORD:
                        setView(View.PASSWORD);
                        break;

                    case OTP_FLOW.FRAUD:
                    case OTP_FLOW.SIGN_IN_WITH_OTP:
                        this.routerStore.changeLocation(this.configRepository.onSuccessRedirectUrl);
                        break;

                    case OTP_FLOW.SIGN_UP:
                    default:
                        if (requestId) {
                            this.saveRequestIdIntoUserStore(requestId);
                        }
                        setSuccessMessage(translationKey, VERIFIED_STATUS);
                        await this.onOtpCompleted();
                }
            } else {
                if (riskLevel === RiskLevels.HIGH) {
                    this.redirectToSignInAndShowError(translationKey);
                } else {
                    stopLoading();
                    setErrorMessage(translationKey);
                }
            }
        }
    };

    @action
    public onOtpCompleted = async () => {
        const {
            otpStore: { onOtpSuccess }
        } = this.userRepository;
        const { setRetryButtonVisibility, stopLoading } = this.otpStateHelper;
        setRetryButtonVisibility(false);
        await onOtpSuccess?.();
        const errorMessages = this.messageRepository.messages.get(MessageGroup.SIGN_UP) || [];
        if (errorMessages.length > 0) {
            // display retry button
            stopLoading();
            setRetryButtonVisibility(true);
        }
    };

    @action
    public resetToDefaultStatus = () => {
        this.otpStateHelper.setInitialStatus().setCodeStatus(DEFAULT_STATUS);
    };

    @action
    public generateOtpCode = async () => {
        const {
            startLoading,
            setErrorMessage,
            setSuccessMessage,
            stopLoading
        } = this.otpStateHelper;

        const {
            otpStore: { data: { hmac } = {} }
        } = this.userRepository;

        startLoading();
        const request = { ...this.requestBase, hmac };
        const success = await this.userRepository.generateCode(request);
        stopLoading();
        const {
            otpStore: { data: { resendWaitingPeriod: period, translationKey = '', flow } = {} }
        } = this.userRepository;
        if (success) {
            if (this.configRepository.otpConfig?.enableCountdown && period && period > 0) {
                this.countdownSeconds = period;
            }
            this.setResendThreshold(); // set threshold before status changed to sent for timer
            setSuccessMessage(translationKey, SENT_STATUS);
        } else {
            const isSignInFlow = flow !== OTP_FLOW.SIGN_UP;

            if (
                isSignInFlow &&
                translationKey === OTP_ERROR_MESSAGE_TOO_MANY_ATTEMPTS_TRANSLATION_KEY
            ) {
                this.redirectToSignInAndShowError(translationKey);
            } else {
                setErrorMessage(translationKey);
            }
        }
    };

    @action
    public goBack = () => {
        if (this.otpStateHelper.isVerified) {
            return;
        }

        const { flow } = this.userRepository.otpStore.data;
        const { setView } = this.userRepository.signInWithOtpFormStore;

        if (flow === OTP_FLOW.RESET_PASSWORD || flow === OTP_FLOW.SIGN_IN_WITH_OTP) {
            setView(View.EMAIL);
        }

        this.userRepository.otpStore.setDefaultData();
    };

    public onInit = async (): Promise<void> => {
        this.userRepository.otpStore.setOtpType(
            this.switchToOtpType === OTP_TYPE_EMAIL ? OTP_TYPE_SMS : OTP_TYPE_EMAIL
        );
        this.resetToDefaultStatus();
        await this.generateOtpCode();
    };

    private loadFromConfiguration = () => {
        const {
            otpConfig: {
                otpLength = DEFAULT_DIGIT_OF_VERIFICATION_CODE,
                enableCountdown = false,
                resendCodeBlockThreshold = DEFAULT_FLASH_DURATION,
                otpType: supportedOtpType
            } = {}
        } = this.configRepository;
        this.numberOfBox = otpLength;
        this.enableCountdown = enableCountdown;
        this.resendCodeBlockThreshold = resendCodeBlockThreshold;
        this.allowSwitch =
            supportedOtpType === OTP_TYPE_EMAIL_SMS || supportedOtpType === OTP_TYPE_SMS_EMAIL;
    };

    private loadFromOtpStore = () => {
        const {
            otpStore: { data: { resendWaitingPeriod: period, lastTime } = {} }
        } = this.userRepository;
        if (this.configRepository.otpConfig?.enableCountdown && period && period > 0 && lastTime) {
            this.countdownSeconds = this.getResendWaitingPeriod(period, lastTime);
        }
    };

    private getResendWaitingPeriod = (period: number, lastTime: string): number => {
        if (!period || !lastTime) {
            return 0;
        }

        const periodTime = Number(period);
        const secondsElapsed = Math.abs(new Date(lastTime).getTime() - new Date().getTime()) / 1000;

        return Math.max(0, Math.round(periodTime - secondsElapsed));
    };

    private redirectToSignInAndShowError = (transKey: string) => {
        this.messageRepository.setMessages(MessageGroup.SIGN_IN, [{ transKey }]);
        this.userRepository.otpStore.setDefaultData();
        this.userRepository.resetView();
    };

    private setResendThreshold = () => {
        const expiresAt = Date.now() + this.resendCodeBlockThreshold;
        const expires = `; max-age=${this.resendCodeBlockThreshold}`;

        setCookie(RESEND_OTP_AFTER, expiresAt.toString(), expires);
    };
}
